import { createCatalogRepository } from '@/features/compliance/catalog/catalogRepository'
import type {
  BookAppointmentInput,
  GrantCreditInput,
} from '@/features/actions/schemas/actionSchemas'
import { seedAppointments, DEVICES } from '@/features/scheduler/demo/demoScheduleSeed'
import { fromMinutes, toMinutes } from '@/features/scheduler/schedulerDates'
import { todayIn } from '@/lib/utils/dates'
import { createShowRepository, showStorageKey } from '@/features/compliance/demo/showModeRepository'
import {
  showRecordSchema,
  type ShowContext,
  type ShowScope,
} from '@/features/compliance/demo/showModeModel'
import {
  creditBalance,
  eligibility,
  startVisit,
  workspaceSchema,
  type ShowWorkspace,
} from '@/features/compliance/demo/showVisitModel'
import { DEMO_PEOPLE } from '@/lib/demo/people'
import { env } from '@/lib/env'
import { useAuthStore } from '@/stores/authStore'

export const DEMO_WORKSPACE_PREFIX = 'ail.show.workspace.v1:'
function sessionFor(locationId?: string) {
  const { session, status } = useAuthStore.getState()
  if (
    !env.complianceDevelopment ||
    status !== 'authenticated' ||
    !session?.location ||
    (locationId && session.location.publicId !== locationId)
  )
    throw new Error('Die Sitzung hat sich geändert. Bitte öffnen Sie die Ansicht erneut.')
  return { ...session, location: session.location }
}
function seed(locationId: string): ShowWorkspace {
  const ws: ShowWorkspace = {
    version: 1,
    revision: 0,
    locationId,
    validityMonths: 6,
    appointments: seedAppointments(),
    credits: {},
    records: {},
  }
  DEMO_PEOPLE.forEach((_, index) => {
    const purchased = 8 + (index % 4) * 5
    let remaining = Math.max(0, 7 - (index % 9))
    ws.credits[String(500 + index)] = { purchased, consumed: purchased - remaining }
    ws.appointments
      .filter((a) => a.customerId === String(500 + index))
      .sort((a, b) => `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`))
      .forEach((a) => {
        a.creditState =
          a.status === 'booked'
            ? remaining-- > 0
              ? 'reserved'
              : 'open'
            : ['done', 'checked_in'].includes(a.status)
              ? 'consumed'
              : null
        a.creditStateLabel =
          a.creditState === 'reserved'
            ? 'Reserviert'
            : a.creditState === 'open'
              ? 'Offen'
              : a.creditState
                ? 'Verbraucht'
                : null
      })
  })
  return workspaceSchema.parse(ws)
}
function read(locationId: string) {
  sessionFor(locationId)
  try {
    const raw = window.localStorage.getItem(DEMO_WORKSPACE_PREFIX + locationId)
    const value = raw === null ? seed(locationId) : workspaceSchema.parse(JSON.parse(raw))
    if (value.locationId !== locationId) throw new Error('scope')
    for (const record of Object.values(value.records))
      for (const document of showRecordSchema.parse(record).documents) {
        if (document.catalog)
          createCatalogRepository(window.localStorage).assertVersion(locationId, document.catalog)
      }
    return value
  } catch {
    throw new Error(
      'Die gespeicherten Termin- und Kontingentdaten sind nicht lesbar. Sie wurden nicht überschrieben.',
    )
  }
}
function save(next: ShowWorkspace, expectedRevision: number) {
  sessionFor(next.locationId)
  if (read(next.locationId).revision !== expectedRevision)
    throw new Error('Die Daten wurden inzwischen geändert. Bitte prüfen Sie erneut.')
  const value = workspaceSchema.parse({ ...next, revision: expectedRevision + 1 })
  try {
    window.localStorage.setItem(DEMO_WORKSPACE_PREFIX + next.locationId, JSON.stringify(value))
  } catch {
    throw new Error(
      'Speichern fehlgeschlagen: Der Browserspeicher ist voll oder gesperrt. Es wurde nichts gebucht.',
    )
  }
}

/** Same-origin tabs serialize the final read/check/write; the shared server uses its own CAS. */
export async function withDemoWorkspaceLock<T>(
  locationId: string,
  action: () => Promise<T> | T,
): Promise<T> {
  sessionFor(locationId)
  if (!navigator.locks)
    throw new Error(
      'Dieser Browser unterstützt die sichere Speicherung nicht. Bitte verwenden Sie einen aktuellen Browser über HTTPS oder localhost.',
    )
  return navigator.locks.request(DEMO_WORKSPACE_PREFIX + locationId, async () => {
    sessionFor(locationId)
    return action()
  })
}

/** Copy-on-write import: the pre-expansion record and its PDFs remain untouched. */
export function workspaceRecordRepository(scope: ShowScope) {
  const key = showStorageKey(scope)
  return createShowRepository({
    getItem(requested) {
      if (requested !== key) throw new Error('Ungültiger Kundenkontext.')
      const ws = read(scope.locationId)
      return ws.records[scope.customerId] === undefined
        ? window.localStorage.getItem(key)
        : JSON.stringify(ws.records[scope.customerId])
    },
    setItem(requested, value) {
      if (requested !== key) throw new Error('Ungültiger Kundenkontext.')
      const ws = read(scope.locationId)
      const record = showRecordSchema.parse(JSON.parse(value))
      ws.records[scope.customerId] = record
      for (const treatment of record.treatments) {
        if (!treatment.appointmentId || !treatment.completedAt) continue
        const appointment = ws.appointments.find(
          (a) => a.id === treatment.appointmentId && a.customerId === scope.customerId,
        )
        if (!appointment || appointment.creditState !== 'consumed')
          throw new Error('Der Behandlungstermin stimmt nicht überein.')
        appointment.status = 'done'
        appointment.statusLabel = 'Erledigt'
      }
      save(ws, ws.revision)
    },
  })
}

export const demoWorkspaceApi = {
  async appointments() {
    const session = sessionFor()
    const ws = read(session.location.publicId)
    return {
      appointments: ws.appointments,
      devices: DEVICES,
      timezone: session.location.timezone,
      revision: ws.revision,
    }
  },
  credits(customerId: string) {
    return creditBalance(read(sessionFor().location.publicId), customerId)
  },
  async check(context: ShowContext, appointmentId: string) {
    const record = await workspaceRecordRepository(context).read(context)
    const ws = read(context.locationId)
    return eligibility(ws, record, appointmentId, context, new Date().toISOString())
  },
  async start(
    context: ShowContext,
    appointmentId: string,
    expectedRecordRevision: number,
    expectedWorkspaceRevision: number,
    signal?: AbortSignal,
  ) {
    return withDemoWorkspaceLock(context.locationId, async () => {
      const record = await workspaceRecordRepository(context).read(context)
      const ws = read(context.locationId)
      if (record.revision !== expectedRecordRevision || ws.revision !== expectedWorkspaceRevision)
        throw new Error(
          'Die Daten wurden inzwischen geändert. Bitte prüfen Sie die Voraussetzungen erneut.',
        )
      signal?.throwIfAborted()
      const next = startVisit(
        ws,
        record,
        appointmentId,
        context,
        new Date().toISOString(),
        crypto.randomUUID(),
      )
      const currentSession = sessionFor(context.locationId)
      if (
        currentSession.user.publicId !== context.actor.id ||
        currentSession.role.key !== context.role
      )
        throw new Error('Die Sitzung hat sich geändert.')
      save(next, ws.revision)
      const appointment = next.appointments.find((a) => a.id === appointmentId)!
      const treatment = showRecordSchema.parse(next.records[context.customerId]).treatments.at(-1)!
      return {
        appointmentId,
        status: appointment.status,
        statusLabel: appointment.statusLabel,
        checkedInAt: treatment.startedAt,
        checkedInBy: context.actor.name,
        creditState: appointment.creditState,
        creditStateLabel: appointment.creditStateLabel,
        credits: creditBalance(next, context.customerId),
      }
    })
  },
  async settings() {
    const ws = read(sessionFor().location.publicId)
    return { months: ws.validityMonths, revision: ws.revision }
  },
  async setValidity(months: number, revision: number) {
    const session = sessionFor()
    return withDemoWorkspaceLock(session.location.publicId, () => {
      if (sessionFor().role.key !== 'branch_manager')
        throw new Error('Nur die Filialleitung kann diese Einstellung ändern.')
      const ws = read(session.location.publicId)
      if (ws.revision !== revision)
        throw new Error('Die Einstellungen wurden geändert. Bitte laden Sie sie erneut.')
      ws.validityMonths = months
      save(ws, revision)
    })
  },
  async create(input: BookAppointmentInput, customerName: string) {
    const session = sessionFor()
    return withDemoWorkspaceLock(session.location.publicId, () => {
      const ws = read(session.location.publicId)
      const device = DEVICES.find((d) => d.id === input.deviceId)
      if (!device) throw new Error('Das Gerät wurde nicht gefunden.')
      const credit = creditBalance(ws, input.subjectId).available > 0
      const appointment = {
        id: String(
          Math.max(3000, ...ws.appointments.map((a) => Number(a.id)).filter(Number.isFinite)) + 1,
        ),
        deviceId: device.id,
        deviceName: device.name,
        customerId: input.subjectId,
        customerName,
        date: input.date,
        start: input.start,
        end: fromMinutes(toMinutes(input.start) + input.durationMinutes),
        kind: input.kind,
        kindLabel: input.kind === 'trial' ? 'Probetermin' : 'Behandlungstermin',
        status: 'booked',
        statusLabel: 'Gebucht',
        creditState: credit ? 'reserved' : 'open',
        creditStateLabel: credit ? 'Reserviert' : 'Offen',
      }
      ws.appointments.push(appointment)
      save(ws, ws.revision)
      return {
        appointment,
        credits: creditBalance(ws, input.subjectId),
        stageChanged: false,
        previousStage: null,
        pipeline: null,
      }
    })
  },
  async grant(customerId: string, input: GrantCreditInput) {
    const session = sessionFor()
    return withDemoWorkspaceLock(session.location.publicId, () => {
      if (sessionFor().role.key !== 'branch_manager')
        throw new Error('Nur die Filialleitung darf Kontingente gutschreiben.')
      if (!Number.isSafeInteger(input.quantity) || input.quantity < 1 || !input.note.trim())
        throw new Error('Bitte prüfen Sie Menge und Begründung.')
      const ws = read(session.location.publicId)
      ws.credits[customerId] ??= { purchased: 0, consumed: 0 }
      ws.credits[customerId].purchased += input.quantity
      const changed = []
      for (const appointment of ws.appointments
        .filter(
          (a) => a.customerId === customerId && a.status === 'booked' && a.creditState === 'open',
        )
        .sort((a, b) => `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`))) {
        if (creditBalance(ws, customerId).available < 1) break
        appointment.creditState = 'reserved'
        appointment.creditStateLabel = 'Reserviert'
        changed.push({
          id: Number(appointment.id),
          previousCreditState: 'open',
          previousCreditStateLabel: 'Offen',
          creditState: 'reserved',
          creditStateLabel: 'Reserviert',
        })
      }
      save(ws, ws.revision)
      return {
        customerPublicId: customerId,
        granted: {
          quantity: input.quantity,
          note: input.note,
          grantedAt: new Date().toISOString(),
          byUserName: session.user.displayName,
        },
        credits: creditBalance(ws, customerId),
        appointmentsChanged: changed,
      }
    })
  },
  today() {
    return todayIn(sessionFor().location.timezone)
  },
}
